import { go } from 'connected-react-router';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Design System
import Form from 'jl-design-system/components/form/Form';
import FormFieldErrorMessage from 'jl-design-system/components/form-field-error-message/FormFieldErrorMessage';
import { UnderlinedButton } from 'jl-design-system/elements/button/Button';
import { resetForm, submitForm, clearFormSubmitErrors } from 'jl-design-system/redux/actions/form/formActions';

// Types
import { ProgressStateType } from 'types/Constants.types';
import { AppDispatch, RootState } from 'types/RootState.types';
import { GiftOptionsFormProps } from './GiftOptionsForm.types';

// Config
import giftOptionsFormState from './GiftOptionsForm.state';
import {
  ANALYTICS_PREPAID_GIFTCARD_MESSAGE,
  ANALYTICS_GIFT_OPTIONS_CHANGE_TO_PREPAID,
} from '@constants/actionConstants';
import { GIFT_OPTIONS } from '@constants/giftOptionsConstants';
import PROGRESS_STATE from '@constants/progressStateConstants';
import { triggerAnalyticsEvent } from '@redux/actions/analytics/analyticsAction';
import { changeGiftOption, redirectFromGiftCardToPayment } from '@redux/actions/payment/paymentActions';
import { getHint, suggestPrepaidCard } from '@redux/reducers/payment/giftOptionsHigherOrderReducer';
import { GIFT_CARD_NUMBER } from '@utils/form/configs/giftFields';

// Components
import Container from '@components/container';

const GiftOptionsForm = ({
  config = { fields: [] },
  dataTest = '',
  disabled = false,
  formContainerClassName,
  formSubmitFailed = false,
  isLoading = false,
  onFormSubmit = () => {},
}: GiftOptionsFormProps) => {
  const dispatch: AppDispatch = useDispatch();
  const formId = (config && config.id) || '';

  const {
    errorMessage = '',
    errorMessageId = '',
    formHasSyncErrors = false,
    fullDataEntered,
    values = {},
  } = useSelector((state: RootState) => giftOptionsFormState(state, formId));

  const [progress, setProgress] = useState<ProgressStateType>(isLoading ? PROGRESS_STATE.LOADING : PROGRESS_STATE.NONE);
  const [hint, setHint] = useState<string>('');
  const [prepaidCardLinkText, setPrepaidCardLinkText] = useState<string>('');
  const oldIsLoading = useRef<boolean>(isLoading);
  const hasSubmittedRef = useRef<boolean>(false);
  const previousValuesRef = useRef<any>(values);
  const hasClearedErrorsRef = useRef<boolean>(false);

  const getNewHint = () => {
    const { giftVoucherSecurityCode, giftVoucherSerialNumber } = values;
    return getHint({ giftVoucherSecurityCode, giftVoucherSerialNumber });
  };

  useEffect(() => {
    const newHint = getNewHint();

    if (!oldIsLoading.current && isLoading) {
      setProgress(PROGRESS_STATE.LOADING);
      setHint(newHint);
    }

    if (oldIsLoading.current && !isLoading) {
      setHint(newHint);
      setProgress(formSubmitFailed ? PROGRESS_STATE.FAILED : PROGRESS_STATE.SUCCESS);
    }

    oldIsLoading.current = isLoading;
  }, [isLoading, formSubmitFailed]);

  useEffect(() => {
    const newHint = getNewHint();

    if (newHint !== hint && hint !== 'gift voucher') {
      setHint(newHint);
    }
  }, [values]);

  useEffect(() => {
    const readyToSubmit = fullDataEntered && !formHasSyncErrors;
    const valuesChanged = JSON.stringify(previousValuesRef.current) !== JSON.stringify(values);

    if (readyToSubmit && !hasSubmittedRef.current) {
      dispatch(submitForm(formId));
      hasSubmittedRef.current = true;
    }

    if (!readyToSubmit || valuesChanged) {
      hasSubmittedRef.current = false;
    }

    previousValuesRef.current = values;
  }, [fullDataEntered, formHasSyncErrors, values]);

  useEffect(() => {
    if (!isLoading && !formSubmitFailed) {
      dispatch(resetForm(formId));
      setProgress(PROGRESS_STATE.NONE);
    }
  }, [isLoading, formSubmitFailed]);

  useEffect(() => {
    const isEmptyValues =
      !values?.giftVoucherSerialNumber?.trim() && !values?.giftVoucherSecurityCode?.trim();

    if (formSubmitFailed && !fullDataEntered && isEmptyValues && !hasClearedErrorsRef.current) {
      dispatch(clearFormSubmitErrors(formId));
      setProgress(PROGRESS_STATE.NONE);
      hasClearedErrorsRef.current = true;
    }

    if (!formSubmitFailed || fullDataEntered) {
      hasClearedErrorsRef.current = false;
    }
  }, [formSubmitFailed, fullDataEntered, values]);

  const onGiftCardNumberKeyUpHandler = (evt: React.KeyboardEvent<HTMLInputElement>) => {
    const currentValues = previousValuesRef.current;

    const newHint = getHint({
      giftCardNumberKey: evt.key,
      giftCardNumberValue: currentValues?.giftCardNumber,
    });
    const prepaidText = suggestPrepaidCard(currentValues?.giftCardNumber);

    if (prepaidText) {
      dispatch(triggerAnalyticsEvent(ANALYTICS_PREPAID_GIFTCARD_MESSAGE));
    }

    setHint(newHint);
    setPrepaidCardLinkText(prepaidText);
  };

  const changeGiftOptionHandler = () => {
    const changeTo = hint === 'gift card' ? GIFT_OPTIONS.GIFT_CARD : GIFT_OPTIONS.GIFT_VOUCHER;
    dispatch(changeGiftOption(changeTo));
  };

  const changeToCardPaymentHandler = () => {
    const currentValues = previousValuesRef.current;
    dispatch(go(-1));
    dispatch(triggerAnalyticsEvent(ANALYTICS_GIFT_OPTIONS_CHANGE_TO_PREPAID));
    dispatch(redirectFromGiftCardToPayment({ cardNumber: currentValues?.giftCardNumber }));
  };

  const extendedFields = config.fields?.map((field) => ({
    ...field,
    progress,
    ...(field.id === GIFT_CARD_NUMBER.id ? { extensionProps: { onKeyUp: onGiftCardNumberKeyUpHandler } } : {}),
  }));

  return (
    <Container className={formContainerClassName}>
      <Form
        config={{ ...config, fields: extendedFields }}
        data-testid={dataTest}
        disabled={disabled}
        form={formId}
        onSubmit={onFormSubmit}
        progress={progress}
        useFormValidationErrorsAlertMessage={false}
      />

      {!hint && !prepaidCardLinkText && errorMessage && (
        <FormFieldErrorMessage
          data-testid="gift-options-form-error"
          id={errorMessageId}
          message={errorMessage}
          roleAlert
        />
      )}

      {hint && !prepaidCardLinkText && (
        <p className="mb-0 mt-1" data-testid={`${dataTest}-hint`}>
          Are you trying to enter a <UnderlinedButton onClick={changeGiftOptionHandler}>{hint}</UnderlinedButton>?
        </p>
      )}

      {prepaidCardLinkText && (
        <Container marginBottom="0" marginTop="1" testId={`${dataTest}-prepaid-hint`}>
          This does not look like a valid gift card number. If you are entering a pre-paid card, please enter{' '}
          <UnderlinedButton data-testid="prepaid-card-hint-button" onClick={changeToCardPaymentHandler}>
            {prepaidCardLinkText}
          </UnderlinedButton>
        </Container>
      )}
    </Container>
  );
};

export default GiftOptionsForm;
